import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Box, Divider, Grid, LinearProgress, Paper, Typography, useTheme } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { UploadItem } from "@/components/common/upload";
import useUploadFile from "@/hooks/use-upload-file";
import type { Doc } from "@/types/models/delivery.types";
import type { DeliveryFormType } from "../types";

type DocumentsComponentV3Props = {
  form: DeliveryFormType;
  folder: string;
  deliveryId?: number;
};

export function DocumentsComponentV3({ form, folder, deliveryId }: DocumentsComponentV3Props) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Grid size={{ xs: 12 }}>
      <Paper
        elevation={4}
        sx={{
          backgroundColor: "#E7E6F7",
          borderRadius: 1,
          padding: "20px",
          height: "100%",
        }}
      >
        <form.AppField name="lastStep.docs">
          {(field) => {
            const docsList: Doc[] = Array.isArray(field.state.value) ? field.state.value : [];

            return (
              <Grid container paddingBottom={2} spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Box alignItems="center" display="flex" justifyContent="space-between">
                    <Typography variant="h5">{t("delivery.form.documents")}</Typography>
                    {!deliveryId && (
                      <Typography color="error" variant="caption">
                        {t("delivery.form.documents-after-creation")}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ bgcolor: "#fe5d8d" }} />
                </Grid>

                {deliveryId && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <DropzoneSection
                        folder={folder}
                        deliveryId={deliveryId}
                        currentDocs={docsList}
                        onFilesUploaded={(newDocs) => {
                          field.handleChange(newDocs);
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ bgcolor: theme.palette.primary.main, my: 2 }} />
                    </Grid>

                    {docsList.map((doc, index) => (
                      <Grid size={{ xs: 12 }} key={doc.key || index}>
                        <UploadItem
                          value={doc}
                          meta={{ folder, subFolder: String(deliveryId) }}
                          canEdit={true}
                          handleDelete={() => {
                            const newDocs = docsList.filter((d) => d.key !== doc.key);
                            field.handleChange(newDocs);
                          }}
                          handleFileUploaded={(updatedDoc) => {
                            const newDocs = [...docsList];
                            newDocs[index] = {
                              ...updatedDoc,
                              label: updatedDoc.label || updatedDoc.name || doc.label || "",
                              type: updatedDoc.type || doc.type || "",
                            } as Doc;
                            field.handleChange(newDocs);
                          }}
                        />
                      </Grid>
                    ))}
                  </>
                )}
              </Grid>
            );
          }}
        </form.AppField>
      </Paper>
    </Grid>
  );
}

function DropzoneSection({
  folder,
  deliveryId,
  currentDocs,
  onFilesUploaded,
}: {
  folder: string;
  deliveryId: number;
  currentDocs: Doc[];
  onFilesUploaded: (docs: Doc[]) => void;
}) {
  const { t } = useTranslation();
  const { onDrop, loading, uploadProgress } = useUploadFile({
    maxFileSize: 10,
    maxFiles: 1,
    uploadedFiles: [],
    metadata: { folder, subFolder: String(deliveryId) },
    onFilesUploaded: (uploadedFiles) => {
      const formattedFiles: Doc[] = uploadedFiles.map((file) => ({
        label: file.name,
        key: file.key,
        url: file.url,
        type: file.type,
      }));
      const newDocs = [...currentDocs, ...formattedFiles];
      onFilesUploaded(newDocs);
    },
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (...args) => {
      void onDrop(...args);
    },
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: "2px dashed",
        borderColor: isDragActive ? "primary.main" : "grey.400",
        borderRadius: 1,
        p: 4,
        textAlign: "center",
        cursor: "pointer",
        bgcolor: isDragActive ? "action.hover" : "transparent",
      }}
    >
      <input {...getInputProps()} />
      <CloudUploadIcon color="primary" fontSize="large" />

      {loading ? (
        <Box sx={{ width: "100%", p: 2 }}>
          <Typography align="center" gutterBottom>
            {t("common.uploading")} ({uploadProgress}%)
          </Typography>
          <LinearProgress value={uploadProgress} variant="determinate" />
        </Box>
      ) : (
        <>
          <Typography variant="body1">Drag & drop files here</Typography>
          <Typography color="textSecondary" variant="caption">
            {t("delivery.form.docs-upload-note")}
          </Typography>
        </>
      )}
    </Box>
  );
}

export default DocumentsComponentV3;
